import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Scanner } from "@yudiel/react-qr-scanner";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { filterBy } from "@progress/kendo-data-query";
import DavetliEtkinlikService from "../../services/srv-davetli/DavetliEtkinlikService";
import DavetliKatilimciService from "../../services/srv-davetli/DavetliKatilimciService";
import { QrScanResult } from "./QrScanResult";
import { QrManualCheckIn } from "./QrManualCheckIn";
import LoadingPanel from "../../components/sys-toolbox/LoadingPanel";
import { Button, Card, CardBody, CardHeader, CardTitle } from "reactstrap";

const SCAN_COOLDOWN_MS = 2000;
const MAX_SCAN_RESULTS = 10;

function createResultId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function QrScanPage() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [eventFilter, setEventFilter] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);

  const scanningLock = useRef(false);
  const unlockTimerRef = useRef(null);
  const scanGenerationRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Operatör yalnızca aktif ve geçerli davetli etkinlikleri görür; filtre sunucu
    // tarafında view üzerinden uygulanır, admin grid'i etkilenmez.
    // Filtreler bilerek küçük yazıldı. DB tarafından gelirken cascade sorunu yaşanmaması için.
    DavetliEtkinlikService.getList({
      take: 1000,
      skip: 0,
      sort: [{ field: "davetlI_ETKINLIK_TANIM_ID", dir: "desc" }],
      filter: {
        logic: "and",
        filters: [
          { field: "aktiF_PASIF", operator: "eq", value: "aktif" },
          { field: "gecerli", operator: "eq", value: "evet" },
        ],
      },
    }).then((res) => {
      if (!mountedRef.current) return;
      setEvents(res?.data?.data || []);
    });

    return () => {
      mountedRef.current = false;
      clearTimeout(unlockTimerRef.current);
    };
  }, []);

  const scheduleUnlock = (generation) => {
    clearTimeout(unlockTimerRef.current);
    unlockTimerRef.current = setTimeout(() => {
      if (scanGenerationRef.current === generation) {
        scanningLock.current = false;
      }
      unlockTimerRef.current = null;
    }, SCAN_COOLDOWN_MS);
  };

  const pushResult = (scanResult) => {
    if (!scanResult) return;
    setResults((prev) =>
      [{ id: createResultId(), ...scanResult }, ...prev].slice(
        0,
        MAX_SCAN_RESULTS,
      ),
    );
  };

  const handleEventChange = (e) => {
    setSelectedEvent(e.value);
    setResults([]);
  };

  const handleScan = async (detectedCodes) => {
    if (scanningLock.current) return;

    const raw = detectedCodes?.[0]?.rawValue;
    if (!raw) return;

    scanningLock.current = true;
    clearTimeout(unlockTimerRef.current);
    const generation = ++scanGenerationRef.current;

    setLoading(true);
    try {
      const scanResult = await DavetliKatilimciService.scanQr({
        QR_TOKEN: raw,
        DAVETLI_ETKINLIK_TANIM_ID:
          selectedEvent?.davetlI_ETKINLIK_TANIM_ID || null,
      });
      if (mountedRef.current && scanGenerationRef.current === generation) {
        pushResult(scanResult);
      }
    } catch {
      // error alert already shown by service
    } finally {
      if (mountedRef.current && scanGenerationRef.current === generation) {
        setLoading(false);
      }
      scheduleUnlock(generation);
    }
  };

  const isEventSelected = !!selectedEvent?.davetlI_ETKINLIK_TANIM_ID;

  return (
    <div>
      {loading && <LoadingPanel />}
      <Card className="mb-3">
        <CardHeader>
          <CardTitle tag="h5">{t("qr_scanner")}</CardTitle>
        </CardHeader>
        <CardBody>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <label style={{ marginBottom: 0, whiteSpace: "nowrap" }}>
              {t("select_guest_event")}
            </label>
            <DropDownList
              data={
                eventFilter ? filterBy(events.slice(), eventFilter) : events
              }
              textField="etkinliK_ADI"
              dataItemKey="davetlI_ETKINLIK_TANIM_ID"
              value={selectedEvent}
              onChange={handleEventChange}
              filterable={true}
              onFilterChange={(e) => setEventFilter(e.filter)}
              style={{ width: "100%", maxWidth: 420, flex: "1 1 240px" }}
              defaultItem={{
                etkinliK_ADI: t("please_select"),
                davetlI_ETKINLIK_TANIM_ID: null,
              }}
            />
            <Button
              color="primary"
              disabled={!isEventSelected}
              onClick={() => setManualOpen(true)}
              style={{ marginLeft: "auto", whiteSpace: "nowrap" }}
            >
              <i className="fa fa-user-plus" style={{ marginRight: 6 }}></i>
              {t("manual_check_in")}
            </Button>
          </div>
        </CardBody>
      </Card>

      {isEventSelected ? (
        <Card className="mb-3">
          <CardBody>
            <div style={{ maxWidth: 420, margin: "0 auto" }}>
              <Scanner
                onScan={handleScan}
                constraints={{ facingMode: "environment" }}
                styles={{ container: { width: "100%" } }}
                paused={loading}
                allowMultiple={true}
                scanDelay={SCAN_COOLDOWN_MS}
              />
            </div>
          </CardBody>
        </Card>
      ) : null}

      {results.length > 0
        ? results.map((item) => <QrScanResult key={item.id} result={item} />)
        : null}

      {manualOpen ? (
        <QrManualCheckIn
          title={t("manual_check_in")}
          toggleDialog={() => setManualOpen(false)}
          event={selectedEvent}
          onAdded={(scanResult) => {
            pushResult(scanResult);
            setManualOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

export { QrScanPage };
